import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { Button } from '@/components/ui/button';
import { UserDisplay } from '@/components/user/UserDisplay';
import { UserSearchPicker, candidateKey } from '@/components/user/UserSearchPicker';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import type { UserGroupSummary } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Plus, Trash2, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/**
 * 나만의 사용자 그룹 — 좌측 그룹 목록 / 우측 멤버 검색·할당. 대시보드 공유 대상으로 쓰인다.
 * 기업 환경 대비 넓은 전체화면 레이아웃.
 */
export function UserGroups() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) navigate({ to: '/login' });
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground-muted text-sm">
        로딩 중…
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <header className="sticky top-0 z-10 border-b border-border bg-background">
          <div className="px-8 py-4 flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/chat' })}>
              <ArrowLeft className="size-4" />
              돌아가기
            </Button>
            <div className="flex items-center gap-2">
              <Users className="size-5 text-accent" />
              <h1 className="text-base font-semibold">나만의 사용자 그룹</h1>
            </div>
          </div>
        </header>
        <main className="flex-1 min-h-0">
          <UserGroupsContent />
        </main>
      </div>
    </ThemeProvider>
  );
}

function UserGroupsContent() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const groupsQuery = useQuery({ queryKey: ['user-groups', 'mine'], queryFn: api.userGroupsList });
  const groups = useMemo(() => groupsQuery.data?.groups ?? [], [groupsQuery.data]);

  // 선택 자동 보정 (첫 그룹 선택 / 삭제 후 보정).
  useEffect(() => {
    if (groups.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !groups.some((g) => g.id === selectedId)) {
      setSelectedId(groups[0]?.id ?? null);
    }
  }, [groups, selectedId]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['user-groups', 'mine'] });
  };

  const createMutation = useMutation({
    mutationFn: (name: string) => api.userGroupCreate({ name }),
    onSuccess: (g) => {
      invalidate();
      setSelectedId(g.id);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '그룹 생성에 실패했습니다.'),
  });

  return (
    <div className="grid h-full grid-cols-[340px_1fr]">
      {/* 좌: 그룹 목록 */}
      <aside className="flex flex-col min-h-0 border-r border-border">
        <div className="px-5 pt-5 pb-3">
          <CreateGroupForm
            onCreate={(name) => {
              setError(null);
              createMutation.mutate(name);
            }}
            pending={createMutation.isPending}
          />
        </div>
        {error ? (
          <div className="mx-5 mb-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}
        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-4">
          {groupsQuery.isLoading ? (
            <p className="px-2 py-3 text-sm text-foreground-muted">불러오는 중…</p>
          ) : groups.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-foreground-muted">
              아직 그룹이 없습니다. 위에서 새 그룹을 만들어 보세요.
            </p>
          ) : (
            <ul className="space-y-0.5">
              {groups.map((g) => (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(g.id)}
                    className={cn(
                      'w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left',
                      selectedId === g.id
                        ? 'bg-accent/10 text-foreground'
                        : 'hover:bg-surface-muted',
                    )}
                  >
                    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
                      <Users className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium truncate">{g.name}</span>
                      <span className="block text-xs text-foreground-muted">
                        멤버 {g.memberCount}명
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* 우: 선택 그룹 상세 */}
      <section className="flex flex-col min-h-0">
        {selectedId ? (
          <GroupDetailPane
            key={selectedId}
            groupId={selectedId}
            summary={groups.find((g) => g.id === selectedId)}
            onChanged={invalidate}
            onDeleted={() => {
              invalidate();
              setSelectedId(null);
            }}
          />
        ) : (
          <div className="grid h-full place-items-center text-sm text-foreground-muted">
            왼쪽에서 그룹을 선택하거나 새로 만드세요.
          </div>
        )}
      </section>
    </div>
  );
}

function CreateGroupForm({
  onCreate,
  pending,
}: {
  onCreate: (name: string) => void;
  pending: boolean;
}) {
  const [name, setName] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const v = name.trim();
        if (!v) return;
        onCreate(v);
        setName('');
      }}
      className="flex items-center gap-2"
    >
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="새 그룹 이름"
        maxLength={80}
        className="h-9 flex-1 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-accent"
      />
      <Button type="submit" size="sm" disabled={pending || !name.trim()}>
        <Plus className="size-4" />
        만들기
      </Button>
    </form>
  );
}

function GroupDetailPane({
  groupId,
  summary,
  onChanged,
  onDeleted,
}: {
  groupId: string;
  summary?: UserGroupSummary;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');

  const detailQuery = useQuery({
    queryKey: ['user-groups', 'detail', groupId],
    queryFn: () => api.userGroupDetail(groupId),
  });
  const detail = detailQuery.data;
  const members = detail?.members ?? [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['user-groups', 'detail', groupId] });
    onChanged();
  };

  const addMutation = useMutation({
    mutationFn: (userIds: string[]) => api.userGroupAddMembers(groupId, userIds),
    onSuccess: (d) => {
      qc.setQueryData(['user-groups', 'detail', groupId], d);
      onChanged();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '멤버 추가에 실패했습니다.'),
  });
  const removeMutation = useMutation({
    mutationFn: (userIds: string[]) => api.userGroupRemoveMembers(groupId, userIds),
    onSuccess: (d) => {
      qc.setQueryData(['user-groups', 'detail', groupId], d);
      onChanged();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '멤버 제거에 실패했습니다.'),
  });
  const renameMutation = useMutation({
    mutationFn: (name: string) => api.userGroupRename(groupId, { name }),
    onSuccess: () => {
      setRenaming(false);
      refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '이름 변경에 실패했습니다.'),
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.userGroupDelete(groupId),
    onSuccess: onDeleted,
    onError: (e) => setError(e instanceof ApiError ? e.message : '그룹 삭제에 실패했습니다.'),
  });

  const excludeKeys = useMemo(
    () => new Set(members.map((m) => candidateKey('user', m.id))),
    [members],
  );

  const title = summary?.name ?? detail?.name ?? '';

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="shrink-0 px-8 pt-6 pb-4 border-b border-border">
        <div className="flex items-center justify-between gap-3">
          {renaming ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const v = renameDraft.trim();
                if (v) renameMutation.mutate(v);
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                maxLength={80}
                className="h-9 w-64 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-accent"
              />
              <Button type="submit" size="sm" disabled={renameMutation.isPending}>
                저장
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(false)}>
                취소
              </Button>
            </form>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-lg font-semibold truncate">{title}</h2>
              <span className="text-sm text-foreground-muted">멤버 {members.length}명</span>
            </div>
          )}
          {!renaming ? (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                aria-label="이름 변경"
                onClick={() => {
                  setRenameDraft(title);
                  setRenaming(true);
                }}
                className="grid size-8 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
              >
                <Pencil className="size-4" />
              </button>
              <button
                type="button"
                aria-label="그룹 삭제"
                onClick={() => {
                  if (
                    window.confirm(
                      `'${title}' 그룹을 삭제할까요? (멤버 구성만 삭제, 사용자는 유지)`,
                    )
                  ) {
                    deleteMutation.mutate();
                  }
                }}
                className="grid size-8 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-destructive"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          ) : null}
        </div>
        <div className="mt-3 max-w-xl">
          <UserSearchPicker
            excludeKeys={excludeKeys}
            includeUsers
            onSelect={(c) => {
              if (c.kind !== 'user') return;
              setError(null);
              addMutation.mutate([c.id]);
            }}
            placeholder="이름 또는 이메일로 검색해서 멤버 추가"
          />
        </div>
        {error ? <div className="mt-2 text-xs text-destructive">{error}</div> : null}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-8 py-4">
        {detailQuery.isLoading ? (
          <p className="text-sm text-foreground-muted">불러오는 중…</p>
        ) : members.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-foreground-muted">
            아직 멤버가 없습니다. 위에서 검색해 사람을 추가하세요.
          </div>
        ) : (
          <ul className="max-w-2xl space-y-0.5">
            {members.map((m) => (
              <li
                key={m.id}
                className="group flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-muted"
              >
                <UserDisplay
                  displayName={m.displayName}
                  email={m.email}
                  attributes={m.attributes}
                  className="flex-1"
                />
                <button
                  type="button"
                  aria-label="멤버 제거"
                  onClick={() => removeMutation.mutate([m.id])}
                  className="shrink-0 grid size-7 place-items-center rounded-md text-foreground-muted opacity-0 group-hover:opacity-100 hover:bg-surface hover:text-destructive"
                >
                  <X className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
